import type { SuperHubAPIDefinitionSlice } from './ApiDefinitions';
import type { SuperHubBranchesSlice } from './Branches';
import type { SuperHubDocumentSlice } from './Document';
import type { SuperHubEditorSlice } from './Editor';
import type { SuperHubLinterSlice } from './Linter';
import type { SuperHubSidebarSlice } from './Sidebar';
import type { SuperHubVersionSlice } from './Versions';
import type { BranchRepresentationType } from '@readme/api-contracts/schemas/branch';
import type { VersionRepresentationType } from '@readme/api-contracts/schemas/version';
import type { ProjectClientSide } from '@readme/backend/models/project/types';
import type { UserSchema } from '@readme/backend/models/user/types';
import type { I18nLanguage } from '@readme/i18n';
import type { SuperHubHashRouteParams, SuperHubRouteParams } from '@routes/SuperHub/types';
import type { History } from 'history';

import { matchPath } from 'react-router-dom';

import { projectStore } from '@core/store/Project';
import { SuperHubHashRoutePaths, SuperHubRoutePaths } from '@routes/SuperHub/types';

import { actionLog, createBoundedUseStore, isClient } from '../util';
import { createCrossFrameStore } from '../util/createCrossFrameStore';

import { createSuperHubAPIDefinitionSlice } from './ApiDefinitions';
import { createSuperHubBranchesSlice } from './Branches';
import { createSuperHubDocumentSlice } from './Document';
import { createSuperHubEditorSlice } from './Editor';
import { createSuperHubLinterSlice } from './Linter';
import { createSuperHubSidebarSlice } from './Sidebar';
import { isBranch, isVersion } from './util';
import { createSuperHubVersionSlice } from './Versions';
import { createDefaultVersion } from './Versions/defaults';

interface SlimProjectObj {
  id: string;
  name: string;
  subdomain: string;
}

interface SuperHubStoreState {
  /**
   * The currently active tab in the AI panel. Used for store-based navigation
   * when the AI panel coexists with settings (where hash routing is unavailable).
   */
  activeAITab: 'chat';

  /**
   * Where the AI panel is currently docked. `'sidebar'` overlays the SuperHub
   * sidebar (default); `'right'` opens the legacy resizable right-side panel.
   */
  aiPanelLocation: 'right' | 'sidebar';

  /**
   * Current project version or branch that is in view, e.g. the `1.0` version
   * object or `1.0_my-branch` branch object.
   */
  currentBranch: BranchRepresentationType | VersionRepresentationType;

  /**
   * Default SuperHub options object for the useReadmeApi hook
   */
  defaultApiOptions: { useLegacyBaseUrl: boolean };

  /**
   * When current project is an Enterprise project, this field becomes an object
   * populated with additional enterprise-related info. Otherwise, it remains
   * `undefined` for non-enterprise projects.
   */
  enterprise?: {
    /**
     * Whether the current project is a child of an Enterprise group.
     */
    isChild: boolean;
    /**
     * Whether the current project is an Enterprise group.
     */
    isGroup: boolean;
    /**
     * Whether the Single Project Enterprise flag is enabled.
     * (The child inherits this flag from the parent group.)
     */
    isSingleProject: boolean;
    /**
     * The current enterprise group project object.
     */
    parent: SlimProjectObj | undefined;
    /**
     * List of all children projects in the Enterprise group,
     * including the current project itself.
     */
    projects: SlimProjectObj[];
  };

  /**
   * Whether or not the SuperHub admin UI is temporarily hidden.
   */
  isAdminUIHidden: boolean;

  /**
   * Whether the AI panel is currently open. This is tracked separately from
   * `layout` to allow the AI panel to coexist with other layouts like settings.
   */
  isAIPanelOpen: boolean;

  /**
   * Indicates whether SuperHub should allow access to bidirectional sync
   */
  isBidiSync: boolean;

  /**
   * Indicates whether the tagged reviewers UI is enabled for branches on
   * this project. Gated on SuperHub being enabled.
   */
  isBranchTaggedReviewersEnabled: boolean;

  /**
   * Whether a feature (css, html, js) is currently being demo-ed
   */
  isDemoing: boolean;

  /**
   * True when pages goes into "edit" mode.
   */
  isEditing: boolean;

  /**
   * Whether git-backed internationalization (i18n) is enabled.
   */
  isI18nEnabled: boolean;

  /**
   * Whether or not the JumpBox is currently open (defaults to false).
   */
  isJumpBoxOpen: boolean;

  /**
   * Whether or not the project has the MDXish flag enabled.
   */
  isMdxishEnabled: boolean;

  /**
   * Whether or not My Developers panel is expanded (defaults to true).
   */
  isMyDevPanelExpanded: boolean;

  /**
   * Whether or not the new iframe structure flag is enabled.
   */
  isNewIframeStructure: boolean;

  /**
   * Whether or not Owlbot chat sidebar is open (defaults to false).
   */
  isOwlbotOpenCached: boolean;

  /**
   * Whether or not the project has the prefetch flag enabled.
   */
  isPrefetchEnabled: boolean;

  /**
   * Indicates that the store has been initialized to its beginning state via
   * `initialize()` action and lets connected subscribers know that it is ready
   * for consumption.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Indicates whether the current route is on the API definitions list view
   * within the Reference section. Mainly used as a convenience that inspects
   * route segments to determine which reference section view to load.
   */
  isReferenceDefinitionsRoute: boolean;

  /**
   * Indicates whether SuperHub is enabled for the current project. Will be
   * `true` for all user roles including both admins and logged-out users.
   */
  isSuperHub: boolean;

  /**
   * Indicates this project is a child project of an parent enterprise group
   * that has merge rules enabled.
   */
  isSuperHubBranchMergeRulesEnabled: boolean;

  /**
   * Indicates whether the new SuperHub Slack integration UI feature flag is enabled.
   */
  isSuperHubSlackEnabled: boolean;

  /**
   * Indicates whether SuperHub Slack notification workflows are enabled.
   */
  isSuperHubSlackNotificationsEnabled: boolean;

  /**
   * Indicates whether the SuperHub typography feature flag is enabled.
   */
  isSuperHubTypographyEnabled: boolean;

  /**
   * True when the user is in viewer (Preview) mode — i.e. no route action is active.
   */
  isViewing: boolean;

  /**
   * Lang short code derived from the current branch or version.
   */
  language: I18nLanguage;

  /**
   * Determines which layout to render the SuperHub in.
   * - `default`: Default layout for the SuperHub.
   * - `aside`: Used when comparing two documents side-by-side.
   * - `aside-standalone`: Same as `aside` but without a top nav bar.
   * - `edit`: Used when creating, updating or reviewing a document.
   * - `settings`: Used when configuring settings. The Hub content is hidden in this layout.
   * - `settings-preview`: Used when configuring settings side-by-side with the Hub content.
   * - `settings-preview-stretch`: Used when configuring settings side-by-side for custom code layouts.
   */
  layout:
    | 'aside-standalone'
    | 'aside'
    | 'default'
    | 'edit'
    | 'settings-preview-stretch'
    | 'settings-preview'
    | 'settings';

  /**
   * A message captured from outside the AI panel (e.g. the sidebar footer's
   * quick-input) that should be sent to the chat as soon as it mounts. The
   * chat consumer is responsible for clearing this back to `null` after
   * dispatching the send.
   */
  pendingChatMessage: string | null;

  /**
   * Stores the previously active hash route when the admin UI is hidden so we can restore it.
   */
  previousAdminUIHash: string | null;

  /**
   * Stores the browser router location captured when entering preview mode so we can
   * return to it when exiting.
   */
  previousPreviewEntry: { hash: string; pathname: string; search: string } | null;

  /**
   * Counter that increments whenever recipe data needs to be refreshed.
   * Components using the legacy Tutorials data-fetching pattern can subscribe
   * to this value and re-fetch when it changes.
   */
  recipeRevalidationCount: number;

  /**
   * Refers to the top-level route action that is being performed on any given
   * section. For example, when navigating to `/update/reference/pet`, the
   * action is `update`, which indicates we are editing a Reference page with the
   * slug named `pet`.
   */
  routeAction: NonNullable<SuperHubRouteParams['action']> | null;

  /**
   * Refers to the top-level route section that is being viewed on the Hub such
   * as `docs`, `reference`, `changelog`, etc. For example. when navigating to
   * `/reference/pet`, the top-level route section is `reference`. This is
   * typically but not always the first segment of the route URL.
   */
  routeSection: NonNullable<SuperHubRouteParams['section']> | null;

  /**
   * Contains the encoded page slug from the hub route that is currently being
   * viewed. For example, when viewing `/docs/getting-started`, the slug is
   * `getting-started`, typically the second segment of the hub route.
   * @example
   * ```ts
   * 'getting-started'
   * 'category%20page'
   * ```
   */
  slug: string | null;

  /**
   * Project subdomain that is currently in view, e.g. `my-project-slug`
   */
  subdomain: string | null;

  /** Info about the current user whether they are logged in or not. */
  user: {
    /** Logged in user's email, e.g. `banjo@kazooie.com`. */
    email: string | null;
    /** Logged in user's ID, e.g. `user.teammateUserId`. */
    id: string | null;
    /** Whether the user is authenticated on password-protected projects. */
    isAuthenticated: boolean;
    /** Whether the user is a ReadMe god user with super powers. */
    isGod: boolean;
    notifications: null | {
      /** Preferences the user has set in their notification_settings. */
      settings?: UserSchema['notification_settings'];
      /** HMAC hash of the user's subscriber ID for securing the Novu notifications inbox. */
      subscriberHash?: string;
    };
    /** Whether the user is an Enterprise group admin. */
    isGroupAdmin: boolean;
    /** Whether the user is a logged in end-user with read-only access. */
    isGroupEndUser: boolean;
    /** Whether the user is the owner of the project. */
    isOwner: boolean;
    /** Logged in user's name, e.g. `Banjo Stevens`. */
    name: string | null;
    /**
     * Indicates the current user's abstracted role which determines how to
     * tailor the UI to expose only features they have access to.
     *
     * NOTE: This is not intended to be an exact represention of all permission
     * levels, but rather a reduced role set derived from `UserBridge` roles and
     * permissions to indicate which SuperHub UI to render.
     *
     * - `admin`: full access to view & edit versions, branches and settings
     * - `end-user`: logged out or end users with read-only access to content
     * - `editor`: team editors with limited access to view & edit branches
     * - `viewer`: team viewers with limited access to view branches
     */
    role: 'admin' | 'editor' | 'end-user' | 'viewer';
    /** User's Stripe customer ID. */
    stripeId: string | null;

    permissions: string[];
  };
}

interface SuperHubStoreAction {
  /**
   * Generate the API endpoint URL with the provided `subdomain` and/or `branch`
   * name. If no argument is provided, it defaults to values that currently
   * exist in the store.
   *
   * To exclude the branch name, set `branch` to an empty string.
   * To exclude the entire branch segment from the URL, set `branch` to `null`.
   *
   * @example
   * ```ts
   * getApiBaseUrl()
   *   => '/subdomain/api-next/v2/branches/1.0'
   * getApiBaseUrl({ subdomain: 'custom', branch: '2.0_branch' })
   *   => '/custom/api-next/v2/branches/2.0_branch'
   * getApiBaseUrl({ branch: '' })
   *   => '/subdomain/api-next/v2/branches'
   * getApiBaseUrl({ branch: null })
   *   => '/subdomain/api-next/v2'
   * ```
   */
  getApiBaseUrl: (args?: {
    subdomain?: SuperHubStoreState['subdomain'];
    branch?: SuperHubStoreState['currentBranch']['name'] | null;
  }) => string;

  /**
   * Returns the branch object that is `currentBranch` when it's populated with
   * a child branch and not a version. Otherwise returns `null`. Use this when
   * you specifically need to work with child branches and not versions.
   * @example
   * ```ts
   * const currentChildBranch = useSuperHubStore(s => getCurrentChildBranch());
   * if (!currentChildBranch) {
   *   // We're not on a branch, so don't try to access branch-only properties.
   *   return;
   * }
   * ```
   */
  getCurrentChildBranch: () => BranchRepresentationType | null;

  /**
   * Returns a version object if `currentBranch` is populated with a version.
   * Otherwise, returns the `currentBranch`'s parent (aka `base`) version. Use
   * this when you specifically need to work with versions and not branches.
   *
   * ⚠️ Note that this should only be used for functionality that is
   * intentionally only available for versions and not branches (e.g. Metrics).
   * @example
   * ```ts
   * const currentVersion = useSuperHubStore(s => getCurrentVersion());
   * const metricsFetch = useMetricsAPIFetcher({
   *   version: currentVersion.name,
   * });
   * ```
   */
  getCurrentVersion: () => VersionRepresentationType;

  /**
   * Initializes the store based on the provided settings. In order to enable
   * SSR, this needs to be called inline and high up in the rendering tree to
   * make the store's state ready before rendering components downstream during
   * both SSR and the initial CSR.
   */
  initialize: (settings: {
    /**
     * The full git-backed representation of the current version or branch. This
     * value is only available for git-backed projects and will be `undefined`
     * for legacy projects.
     */
    currentBranch?: SuperHubStoreState['currentBranch'];

    language?: SuperHubStoreState['language'];

    /** Whether the current project is a child of an Enterprise group. */
    isEnterpriseChild?: boolean;

    /** Whether the current project is an Enterprise group. */
    isEnterpriseGroup?: boolean;

    /** Enterprise group parent object. */
    enterpriseParent?: SlimProjectObj;

    /** Whether the AI panel is open. */
    isAIPanelOpen?: boolean;

    /** Where the AI panel is docked: sidebar overlay (default) or right-side panel. */
    aiPanelLocation?: SuperHubStoreState['aiPanelLocation'];

    /** List of sibling projects in current Enterprise group.  */
    enterpriseProjects?: SlimProjectObj[];

    /**
     * Browser location pathname and hash to initialize the store to.
     * @example
     * ['/docs/getting-started', '#/settings/appearance/theme-editor']
     */
    location?: [pathname: string, hash?: string];

    /**
     * Project feature flags that are relevant to SuperHub in order to determine
     * whether it should be enabled or not.
     */
    projectFlags: Partial<ProjectClientSide['flags']>;

    /**
     * Project subdomain.
     * @example my-project
     */
    subdomain: SuperHubStoreState['subdomain'];

    /** Info about the currently logged in user. */
    user?: SuperHubStoreState['user'];
  }) => void;

  /**
   * Registers the BrowserRouter history instance so hiding admin UI can use
   * `replace` instead of `location.hash =`, avoiding an extra history entry
   * (and an extra back click) when entering preview.
   * Pass `null` on unmount.
   */
  registerBrowserRouterHistory: (history: History | null) => void;

  /**
   * Registers the callback used by {@link toggleSettingsSidebarRailCollapsed}.
   * Pass `null` on unmount.
   */
  registerSettingsSidebarRailToggle: (toggle: (() => void) | null) => void;

  /**
   * Resets the entire store back to its initial default state.
   */
  reset: () => void;

  /**
   * Signals that recipe data should be re-fetched by incrementing the
   * revalidation counter.
   */
  revalidateRecipes: () => void;

  /**
   * Sets the active AI panel tab for store-based navigation.
   */
  setActiveAITab: (tab: SuperHubStoreState['activeAITab']) => void;

  /**
   * Sets where the AI panel is docked. Persisted to localStorage so the
   * preference survives reloads.
   */
  setAIPanelLocation: (location: SuperHubStoreState['aiPanelLocation']) => void;

  /**
   * Sets whether the project is currently demoing a feature or not
   */
  setIsDemoing: (isDemo: boolean) => void;

  /**
   * Captures a message to dispatch into the chat as soon as it mounts. Used by
   * the sidebar footer's quick-input. Pass `null` to clear after consumption.
   */
  setPendingChatMessage: (message: SuperHubStoreState['pendingChatMessage']) => void;

  /**
   * Stores or clears the browser location to restore when exiting preview after entering from edit/review.
   */
  setPreviousPreviewEntry: (entry: SuperHubStoreState['previousPreviewEntry']) => void;

  /**
   * Toggles the `isAdminUIHidden` state.
   * @param restorePreviousHash When showing admin UI again, whether to restore the hash saved when hiding (default true). Set false when the browser URL already reflects the desired hash (e.g. back/forward).
   */
  toggleAdminUIHidden: (nextState?: boolean, options?: { restorePreviousHash?: boolean }) => void;

  /**
   * Toggles the `isAIPanelOpen` state. Pass a boolean to set it explicitly,
   * or omit to toggle.
   */
  toggleAIPanel: (open?: boolean) => void;

  /**
   * Toggles the `isJumpBoxOpen` state.
   */
  toggleJumpBox: (nextState?: boolean) => void;

  /**
   * Toggles the `isOwlbotOpenCached` state.
   */
  toggleOwlbot: () => void;

  /**
   * Invokes the settings sidebar rail collapse toggle when registered by
   * {@link registerSettingsSidebarRailToggle} (see `SidebarNav`).
   */
  toggleSettingsSidebarRailCollapsed: () => void;

  /**
   * Toggles the `isMyDevPanelExpanded` state.
   */
  updateIsMyDevPanelExpanded: () => void;

  /**
   * Updates the language property.
   */
  updateLanguage: (language: I18nLanguage) => void;

  /**
   * Updates the current route we've currently navigated to with the provided
   * URL pathname and hash properties. Segments in each property are then parsed
   * and interpreted by our state.
   * @see ConnectSuperHubStoreToRouter
   */
  updateRoute: (pathname: string, hash?: string) => void;
}

export const AI_PANEL_STORAGE_KEY = 'ai-panel-open';
export const AI_PANEL_LOCATION_STORAGE_KEY = 'ai-panel-location';

export type SuperHubStore = SuperHubAPIDefinitionSlice &
  SuperHubBranchesSlice &
  SuperHubDocumentSlice &
  SuperHubEditorSlice &
  SuperHubLinterSlice &
  SuperHubSidebarSlice &
  SuperHubStoreAction &
  SuperHubStoreState &
  SuperHubVersionSlice;

const initialState: SuperHubStoreState = {
  currentBranch: {
    // Default our initial branch to `stable` to cover cases like for enterprise
    // groups where they are technically version-less.
    ...createDefaultVersion({ name: 'stable' }),
    source: 'readme',
    updated_at: '',
    uri: '/branches/stable',
  },
  language: 'en',
  defaultApiOptions: {
    useLegacyBaseUrl: false,
  },
  enterprise: undefined,
  isBidiSync: false,
  isDemoing: false,
  isEditing: false,
  isViewing: true,
  isAdminUIHidden: false,
  previousAdminUIHash: null,
  previousPreviewEntry: null,
  isI18nEnabled: false,
  isMyDevPanelExpanded: true,
  isMdxishEnabled: false,
  recipeRevalidationCount: 0,
  isNewIframeStructure: false,
  isPrefetchEnabled: false,
  isOwlbotOpenCached: false,
  activeAITab: 'chat',
  aiPanelLocation: 'sidebar',
  isAIPanelOpen: false,
  pendingChatMessage: null,
  isJumpBoxOpen: false,
  isReady: false,
  isReferenceDefinitionsRoute: false,
  isSuperHub: false,
  isSuperHubBranchMergeRulesEnabled: false,
  isBranchTaggedReviewersEnabled: false,
  isSuperHubSlackEnabled: false,
  isSuperHubSlackNotificationsEnabled: false,
  isSuperHubTypographyEnabled: false,
  layout: 'default',
  routeAction: null,
  routeSection: null,
  slug: null,
  subdomain: null,
  user: {
    email: null,
    id: null,
    isAuthenticated: false,
    isGod: false,
    notifications: null,
    isGroupAdmin: false,
    isGroupEndUser: false,
    isOwner: false,
    name: null,
    role: 'end-user',
    stripeId: null,
    permissions: [],
  },
};

/**
 * Vanilla store that contains all application-level state data required by
 * SuperHub. This store can be accessed and used anywhere. React components
 * should call `useSuperHubStore()` instead.
 * @example
 * import { superHubStore } from '@core/store';
 *
 * const isEditing = superHubStore.getState().isEditing;
 */
export const superHubStore = createCrossFrameStore<SuperHubStore>('SuperHubStore', (set, get, ...props) => {
  /**
   * Holds reference to the initial state so we can support resetting the
   * store back to this state when calling `reset()`.
   */
  const resetState = {
    ...createSuperHubAPIDefinitionSlice(set, get, ...props),
    ...createSuperHubDocumentSlice(set, get, ...props),
    ...createSuperHubEditorSlice(set, get, ...props),
    ...createSuperHubSidebarSlice(set, get, ...props),
    ...createSuperHubVersionSlice(set, get, ...props),
    ...createSuperHubBranchesSlice(set, get, ...props),
    ...createSuperHubLinterSlice(set, get, ...props),
    ...initialState,
  };

  let settingsSidebarRailToggle: (() => void) | null = null;
  let browserRouterHistoryRef: History | null = null;

  const replaceLocationHashWithoutHistoryEntry = (nextHash: string) => {
    if (browserRouterHistoryRef) {
      const { pathname, search } = browserRouterHistoryRef.location;
      browserRouterHistoryRef.replace({ pathname, search, hash: nextHash });
      return;
    }
    if (isClient && typeof window.history.replaceState === 'function') {
      const { pathname, search } = window.location;
      window.history.replaceState(window.history.state, '', `${pathname}${search}${nextHash}`);
    }
  };

  return {
    ...resetState,

    getApiBaseUrl: (args = {}) => {
      const { subdomain: currentSubdomain, currentBranch } = get();
      const subdomain = args.subdomain || currentSubdomain;
      if (args.branch === null) {
        return `/${subdomain}/api-next/v2`;
      } else if (args.branch === '') {
        return `/${subdomain}/api-next/v2/branches`;
      }

      const branch = args.branch || currentBranch.name;
      return `/${subdomain}/api-next/v2/branches/${encodeURIComponent(branch)}`;
    },

    getCurrentChildBranch: () => {
      const { currentBranch } = get();
      return isBranch(currentBranch) ? currentBranch : null;
    },

    getCurrentVersion: () => {
      const { currentBranch } = get();
      return isVersion(currentBranch) ? currentBranch : currentBranch.base;
    },

    initialize: settings => {
      // When store is initializing during SSR, we have to explicitly reset
      // it to its initial state. Otherwise, there could be remnants of
      // stale data leftover in the store from a previous SSR. This is b/c
      // our store is a singleton and never gets destroyed on the server.
      if (!isClient) get().reset();

      const {
        currentBranch,
        isAIPanelOpen,
        aiPanelLocation,
        language,
        isEnterpriseChild,
        isEnterpriseGroup,
        enterpriseParent,
        enterpriseProjects = [],
        location = [],
        projectFlags: flags,
        subdomain,
        user,
      } = settings;
      set(
        state => {
          if (currentBranch) {
            state.currentBranch = currentBranch;
          }
          state.language = language || 'en';

          state.isReady = isClient;
          state.isSuperHub = !!flags.superHub;
          state.isMdxishEnabled = !!flags.mdxish;
          state.isNewIframeStructure = !!flags.newIframeStructure;
          state.isPrefetchEnabled = !!flags.prefetch;
          state.isI18nEnabled =
            state.isSuperHub && !!flags.gitTranslations && projectStore.getState().data.i18n.state === 'enabled';
          state.isSuperHubBranchMergeRulesEnabled = state.isSuperHub && !!flags.superHubBranchMergeRules;
          state.isBranchTaggedReviewersEnabled = state.isSuperHub && !!flags.branchTaggedReviewers;
          state.isSuperHubSlackEnabled = state.isSuperHub && !!flags.superHubSlack;
          state.isSuperHubSlackNotificationsEnabled =
            state.isSuperHubSlackEnabled && !!flags.superHubSlackNotifications;
          state.isSuperHubTypographyEnabled = state.isSuperHub && !!flags.superHubTypography;
          state.isBidiSync = state.isSuperHub && user?.role === 'admin' && !!flags.bidiSync;
          state.subdomain = subdomain;
          state.user = {
            email: user?.email || null,
            id: user?.id || null,
            isAuthenticated: user?.isAuthenticated ?? false,
            isGod: user?.isGod ?? false,
            notifications: user?.notifications ?? null,
            isGroupAdmin: user?.isGroupAdmin ?? false,
            isGroupEndUser: user?.isGroupEndUser ?? false,
            isOwner: user?.isOwner ?? false,
            name: user?.name || null,
            role: state.isSuperHub && user ? user.role : 'end-user',
            stripeId: user?.stripeId || null,
            permissions: user?.permissions ?? [],
          };

          if (isAIPanelOpen !== undefined) {
            state.isAIPanelOpen = isAIPanelOpen;
          }

          if (aiPanelLocation !== undefined) {
            state.aiPanelLocation = aiPanelLocation;
          }

          if (isClient && window.localStorage) {
            const savedOwlbotState = window.localStorage.getItem('owlbot-open');
            if (savedOwlbotState !== null) {
              state.isOwlbotOpenCached = savedOwlbotState === 'true';
            }
          }

          if (isEnterpriseGroup || isEnterpriseChild) {
            state.enterprise = {
              isGroup: !!isEnterpriseGroup,
              isChild: !!isEnterpriseChild,
              isSingleProject: 'singleProjectEnterprise' in flags && !!flags.singleProjectEnterprise,
              parent: enterpriseParent,
              projects: enterpriseProjects,
            };
          }
        },
        false,
        actionLog('initialize', settings),
      );

      // Continue initialization based on the location route.
      const [pathname, hash] = location;
      if (pathname) {
        get().updateRoute(pathname, hash);
      }
    },

    updateLanguage: language => {
      set({ language }, false, actionLog('updateLanguage', language));
      get().versions.updateI18nLanguageFilter();
    },

    reset: () => {
      settingsSidebarRailToggle = null;
      browserRouterHistoryRef = null;
      set(resetState, false, actionLog('reset'));
      get().sidebar.updateCache();
    },

    setIsDemoing: isDemo => {
      if (get().isDemoing === isDemo) return;

      set(
        state => {
          state.isDemoing = isDemo;
        },
        false,
        actionLog('isDemoing', isDemo),
      );
    },

    setPreviousPreviewEntry: entry => {
      set(
        state => {
          state.previousPreviewEntry = entry;
        },
        false,
        actionLog('setPreviousPreviewEntry', entry),
      );
    },

    toggleAdminUIHidden: (nextState, options) => {
      if (typeof window === 'undefined') return;

      const { isAdminUIHidden, previousAdminUIHash } = get();
      const shouldHide = nextState ?? !isAdminUIHidden;
      const restorePreviousHash = options?.restorePreviousHash ?? true;

      const currentHash = window.location.hash;
      const isMeaningfulHash = currentHash && currentHash !== '#/' && currentHash !== '';

      set(
        state => {
          state.isAdminUIHidden = shouldHide;
          if (shouldHide) {
            state.isJumpBoxOpen = false;
            state.previousAdminUIHash = isMeaningfulHash ? currentHash : null;
          } else {
            state.previousAdminUIHash = null;
          }
        },
        false,
        actionLog('toggleAdminUIHidden', nextState),
      );

      // Replace the hash so we do not push a new history entry (avoids needing
      // two "back" clicks to leave preview).
      if (shouldHide) {
        replaceLocationHashWithoutHistoryEntry('#/');
      } else if (restorePreviousHash && previousAdminUIHash && previousAdminUIHash !== '#/') {
        replaceLocationHashWithoutHistoryEntry(previousAdminUIHash);
      }
    },

    setActiveAITab: tab => {
      if (get().activeAITab === tab) return;
      set(
        state => {
          state.activeAITab = tab;
        },
        false,
        actionLog('setActiveAITab', tab),
      );
    },

    setAIPanelLocation: location => {
      if (get().aiPanelLocation === location) return;
      set(
        state => {
          state.aiPanelLocation = location;
          if (isClient && window.localStorage) {
            window.localStorage.setItem(AI_PANEL_LOCATION_STORAGE_KEY, location);
          }
        },
        false,
        actionLog('setAIPanelLocation', location),
      );
    },

    setPendingChatMessage: message => {
      if (get().pendingChatMessage === message) return;
      set(
        state => {
          state.pendingChatMessage = message;
        },
        false,
        actionLog('setPendingChatMessage', message),
      );
    },

    toggleAIPanel: open => {
      const nextOpen = open ?? !get().isAIPanelOpen;
      set(
        state => {
          state.isAIPanelOpen = nextOpen;
          if (!nextOpen) {
            state.activeAITab = 'chat';
            // reset the dock to the sidebar on close
            state.aiPanelLocation = 'sidebar';
          }

          if (isClient && window.localStorage) {
            window.localStorage.setItem(AI_PANEL_STORAGE_KEY, nextOpen ? 'true' : 'false');
            if (!nextOpen) {
              window.localStorage.removeItem(AI_PANEL_LOCATION_STORAGE_KEY);
            }
          }
        },
        false,
        actionLog('toggleAIPanel', open),
      );
    },

    toggleJumpBox: nextState => {
      set(
        state => {
          state.isJumpBoxOpen = nextState ?? !state.isJumpBoxOpen;
        },
        false,
        actionLog('toggleJumpBox', nextState),
      );
    },

    registerBrowserRouterHistory: history => {
      browserRouterHistoryRef = history;
    },

    registerSettingsSidebarRailToggle: toggle => {
      settingsSidebarRailToggle = toggle;
    },

    toggleSettingsSidebarRailCollapsed: () => {
      settingsSidebarRailToggle?.();
    },

    updateIsMyDevPanelExpanded: () => {
      set(
        state => {
          state.isMyDevPanelExpanded = !state.isMyDevPanelExpanded;
        },
        false,
        actionLog('updateIsMyDevPanelExpanded'),
      );
    },

    toggleOwlbot: () => {
      set(
        state => {
          state.isOwlbotOpenCached = !state.isOwlbotOpenCached;

          if (isClient && window.localStorage) {
            window.localStorage.setItem('owlbot-open', state.isOwlbotOpenCached ? 'true' : 'false');
          }
        },
        false,
        actionLog('toggleOwlbot'),
      );
    },

    updateRoute: (pathname, hash = '') => {
      // Match the same route paths from our superhub routers to parse out
      // route params in the same way.
      const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
      const hashMatch = matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));

      const {
        params: { action = null, section = pathname === '/' ? 'home' : null, slug = null },
      } = match ?? { params: {} };
      const {
        params: { action: hashAction, section: hashSection },
      } = hashMatch ?? { params: {} };

      set(
        state => {
          state.isEditing = ['create', 'update', 'compare'].includes(action || '');
          state.isViewing = action === null;
          state.isDemoing = false;
          state.isReferenceDefinitionsRoute = action === 'update' && section === 'reference' && !slug;
          state.routeAction = action;
          state.routeSection = section;
          // React router's `useLocation` hook returns a decoded pathname that
          // gets parsed into decoded params, but we want to preserve params in
          // their original encoded form. This makes it easier to match slugs to
          // the encoded slugs returned from our API. If this behavior ever
          // changes however, avoid double-encoding an already encoded slug by
          // first decoding it, then re-encoding.
          state.slug = slug ? encodeURIComponent(decodeURIComponent(slug)) : null;
          state.editor.isCreateNewPage = action === 'create';

          // ⚠️ Be mindful of order when updating!! Hash actions should be
          // evaluated before actions, to ensure that hash action based
          // panel overlays take precedense over the main action
          if (action === 'compare') {
            state.layout = 'aside-standalone';
            state.isAIPanelOpen = false; // The AI panel is never shown on the compare route
          } else if (
            hashAction === 'settings' ||
            hashAction === 'branches' ||
            hashAction === 'content' ||
            hashAction === 'docs-audit' ||
            hashAction === 'my-developers'
          ) {
            state.layout = 'settings';
          } else if (
            hashAction === 'appearance' &&
            (hashSection?.includes('custom-code') || hashSection === 'landing-page')
          ) {
            state.layout = 'settings-preview-stretch';
          } else if (hashAction === 'appearance') {
            state.layout = 'settings-preview';
          } else if (['create', 'update', 'review'].includes(action ?? '')) {
            state.layout = 'edit';
          } else {
            state.layout = 'default';
          }

          if (
            ['settings', 'branches', 'content', 'appearance', 'my-developers', 'docs-audit'].includes(hashAction ?? '')
          ) {
            // Always clear project store errors when re-routing.
            projectStore.getState().resetSaveError();
          }
        },
        false,
        actionLog('updateRoute', { pathname, hash }),
      );
    },
    revalidateRecipes: () => {
      set(
        state => {
          state.recipeRevalidationCount += 1;
        },
        false,
        actionLog('revalidateRecipes'),
      );
    },
  };
});

/**
 * Bound react hook to access our SuperHub store. Must be called within a React
 * component. To access the store outside of React, use `superHubStore` instead.
 * @example
 * import { useSuperHubStore } from '@core/store';
 *
 * function Component() {
 *   const isEditing = useSuperHubStore(s => s.isEditing);
 * }
 */
export const useSuperHubStore = createBoundedUseStore(superHubStore);

export * from './ApiDefinitions';
export * from './Branches';
export * from './ConnectSuperHubStoreToRouter';
export * from './Document';
export * from './Editor';
export * from './InitializeSuperHubStore';
export * from './Sidebar';
export * from './Versions';
